import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  FocusEvent,
} from 'react';

import TextField from '@wetransfer/transfer/component/form/element/textfield';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import I18n from '@wetransfer/transfer/modules/i18n';
import { TransferNormalized } from '@wetransfer/transfer/spaces-bundle/store/types';

export interface Props {
  transfer: TransferNormalized;
  handleDisplayNameChange(value: string): void;
  showAddDisplayNameTip?(): void;
  isForwardedTransfer: boolean;
  userHasTypedTitle: boolean;
  currentDisplayNameInput: string;
  updateUserHasTypedState(): void;
  displayNameDisabled: boolean;
  onFocus?(): void;
}

export const DisplayNameField: React.FC<Props> = ({
  transfer,
  handleDisplayNameChange,
  showAddDisplayNameTip,
  isForwardedTransfer,
  userHasTypedTitle,
  currentDisplayNameInput,
  updateUserHasTypedState,
  displayNameDisabled,
  onFocus,
}) => {
  const [value, setValue] = useState(currentDisplayNameInput || '');
  const [shouldUpdateDisplayName, setShouldUpdateDisplayName] = useState(false);

  const displayNameInput = useRef(null);

  const DISPLAY_NAME_MAX_LENGTH = 255;

  const firstFileInTransfer = transfer?.items[0];
  const transferFolder = transfer?.itemsTree?.items[0];

  const firstTransferItemName = transferFolder
    ? transferFolder?.name?.substring(0, DISPLAY_NAME_MAX_LENGTH)
    : firstFileInTransfer?.name?.substring(0, DISPLAY_NAME_MAX_LENGTH);

  // On first render, if we already have a file in the transfer window, we can assume we are returning from another state
  // We can then persist whatever title the user has entered without it rerendering.
  useEffect(() => {
    const shouldSkipSettingDisplayName =
      firstFileInTransfer &&
      transfer.displayName &&
      value !== firstTransferItemName;

    if (shouldSkipSettingDisplayName || value) {
      updateUserHasTypedState();
    }
    setShouldUpdateDisplayName(true);
  }, []);

  useEffect(() => {
    //Only run this useEffect after the first one ran to ensure we have the right userHasTyped value
    if (shouldUpdateDisplayName) {
      if (isForwardedTransfer || (!isForwardedTransfer && userHasTypedTitle)) {
        setValue(transfer.displayName);
        handleDisplayNameChange(transfer.displayName);

        return;
      }

      if (!userHasTypedTitle) {
        // Set the input value to the first file name when a user adds a file for the first time
        // or if they remove the current first file.
        if (firstFileInTransfer) {
          setValue(firstTransferItemName);
          handleDisplayNameChange(firstTransferItemName);
        } else {
          setValue('');
          handleDisplayNameChange('');
        }
      }
    }
  }, [shouldUpdateDisplayName, firstFileInTransfer, isForwardedTransfer]);

  // Show the tooltip anytime the user clears the title field.
  useEffect(() => {
    const inputHasFocus = document.activeElement === displayNameInput.current;

    const shouldShowDisplayNameTip =
      inputHasFocus && value === '' && showAddDisplayNameTip;

    if (shouldShowDisplayNameTip) {
      showAddDisplayNameTip();
    }
  }, [value]);

  const displayNameFieldProps = {
    type: 'text',
    key: 'displayName',
    name: 'displayName',
    disabled: displayNameDisabled,
    className: !Browser.isMobile ? 'textfield--borderbottom' : null,
    label: !Browser.isMobile
      ? I18n.t('page.transfer.display_name.label')
      : I18n.t('page.transfer.display_name.label_mobile'),
    value: value,
    fieldRef: displayNameInput,
    maxLength: DISPLAY_NAME_MAX_LENGTH,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
  };

  function handleOnFocus() {
    onFocus && onFocus();
  }

  function handleOnBlur(event: FocusEvent<HTMLInputElement>) {
    handleDisplayNameChange(event.target.value.trim());
  }

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    updateUserHasTypedState();
    setValue(event.target.value);
  }

  return (
    <div className="uploader__display-name">
      <TextField {...displayNameFieldProps} />
    </div>
  );
};
